import { HierarchyDetails } from '@components/UI-Components/HierarchyWiseItems'
import { Column } from '@components/UI-Components/HierarchyWiseItemsTable/models/column'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { HIERARCHY_ROOT_ID } from '@modules/order/constants/uiConstants'
import { nav } from '@stores/nav'
import PaginatedTableHierarchyItemsUIStore from '@stores/paginated-table-hierarchy-items/PaginatedTableHierarchyItemsUIStore'
import { productCurveStore } from '@stores/productCurvesStore'
import { getEnumValue } from '@utils/get-enum-value'
import { extractIndexFromId } from '@utils/index-from-id'
import { omitSingle } from '@utils/array-utils'
import formatCurrencyValueByLocale from '@utils/format-currency-value-by-locale'
import formatNumberByLocale from '@utils/format-number-by-locale'
import { differenceBy, reduce } from 'lodash'
import find from 'lodash/find'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import filter from 'lodash/filter'
import mapValues from 'lodash/mapValues'
import omit from 'lodash/omit'
import sum from 'lodash/sum'
import isEmpty from 'lodash/isEmpty'
import values from 'lodash/values'
import has from 'lodash/has'
import { action, computed, observable, transaction } from 'mobx'
import { computedFn } from 'mobx-utils'
import { ListDataStore } from './ListDataStore'
import ListViewTableService, { ListTableView } from './ListViewTableService'
import OrderCellActiveCellItemStore from './OrderCellActiveCellItemStore'
import { orderKPIGlobalTopBarStore } from '../../OrderKPIGlobalTopBarStore'
import { QuantityType } from '@components/UI-Components/QuantityType/constants/QuantityType'
import {
  OrderTemplatePreferences,
  orderTemplatePreferencesStore
} from '../../order/OrderTemplatePreferencesStore'
import { getKPICurrency } from '@modules/order/utils/get-kpi-currency'

export interface ISize {
  id: string
  name: string
  sizeCode: number
  isInactive?: boolean
}

export class ListUIStore extends PaginatedTableHierarchyItemsUIStore<ListDataStore> {
  activeItemStore = new OrderCellActiveCellItemStore()

  @observable isValueSelected = false
  @observable showProductSizes: {
    [productId: string]: boolean
  } = {}

  @action
  setIsValueSelected = (event, checked) => {
    this.isValueSelected = event?.target?.checked || checked
  }

  tableService = new ListViewTableService(
    nav.queryParams.subSection ||
      (nav.queryParams.clientType === getEnumValue(VIPClientType.Internal)
        ? ListTableView.Summary
        : ListTableView.Quantity),
    nav.queryParams.clientType === getEnumValue(VIPClientType.Internal)
      ? [ListTableView.Summary, ListTableView.Quantity]
      : [ListTableView.Quantity],
    {}
  )

  @computed get hierarchyDetails(): HierarchyDetails {
    if (!this.dataStore.heirarchy?.length) {
      return {
        [`${HIERARCHY_ROOT_ID}`]: {
          children: [],
          startIndex: null,
          endIndex: null,
          depth: -1
        }
      }
    }
    // omit finished key from heirarchy details
    const heirarchy: any = omit(
      this.generateTableDataRecursively(this.dataStore.heirarchy, '', 0, 0, 0),
      'finished'
    )
    return {
      [HIERARCHY_ROOT_ID]: {
        children: map(this.dataStore.heirarchy, 'name')
      },
      ...heirarchy
    }
  }

  /**
   * Get Doorwise total kpi's to show in the header rows of quantity view.
   */
  getHeaderDoorKpis = computedFn((doorId: string, quantityType: string) => {
    const fieldType = QuantityType.Size === quantityType ? 'Sized' : 'Door'
    const fieldTypeToMap = QuantityType.Size === quantityType ? 'Size' : 'Draft'

    const doorKpis =
      this.dataStore.heirarchy?.[0]?.orderTotalKpis?.doorKpis?.[doorId] ?? {}
    const { summary, otbData } = orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar
    const currency = getKPICurrency(nav.queryParams.orderType, summary)
    const doorKPI = {
      doorOTB: formatCurrencyValueByLocale(
        currency ?? '',
        otbData?.doorIds?.[doorId]?.OTBValue ?? 0
      ),
      [`total${fieldType}Value`]: formatCurrencyValueByLocale(
        currency ?? '',
        doorKpis?.[`totalValueBy${fieldTypeToMap}Quantity`] ?? 0
      ),
      [`total${fieldType}Quantity`]: `${formatNumberByLocale(
        doorKpis?.[`totalBy${fieldTypeToMap}Quantity`] ?? 0
      )}`,
      numberOfACs: `${formatNumberByLocale(doorKpis?.totalProducts ?? 0)}`
    }
    return doorKPI
  })

  getGroupObject = computedFn(
    (name, depth, children, startIndex, endIndex, totalItems) => ({
      name,
      isLeaf: false,
      depth: depth,
      children,
      // startIndex: acc.finished,
      // endIndex: maxBy(values(res), (val: any) =>
      //   val.name.includes(groupName) ? val.endIndex : 0
      // ),
      totalItems
    })
  )

  getChildObject = computedFn(
    (name, depth, children, startIndex, endIndex, totalItems) => ({
      name,
      isLeaf: false,
      depth: depth,
      children,
      startIndex,
      endIndex,
      totalItems
    })
  )

  @action
  updateSummaryDraftQuantity = (
    product: any,
    newValue: number,
    pathToUpdate?: string
  ) => {
    this.dataStore.updateDraftQuantity(
      product.id,
      {
        draftQuantity: newValue,
        productHierarchy: this.getFocusedProductHierarchy(
          this.dataStore.itemIndexObjectStore.getIndexByKey(product.id)
        )
      },
      pathToUpdate || 'summary.orderProduct',
      false
    )
  }

  @action
  updateQuantityDraftQuantity = async (
    product: any,
    newValue: number,
    selectedGrading: any,
    rollUp?: boolean
  ) => {
    await this.dataStore.updateDraftQuantity(
      product.id,
      {
        draftQuantity: newValue,
        gradingAttributes: selectedGrading ?? {},
        productHierarchy: this.getFocusedProductHierarchy(
          this.dataStore.itemIndexObjectStore.getIndexByKey(product.id)
        )
      },
      'quantity',
      rollUp
    )
  }

  @action
  rollUpAllQuantities = () => {
    this.dataStore.rollUpAllQuantities()
  }
  // *********** Quantity Sub Section ***********
  @action
  hideShowProductSizes = (flag: boolean, productId: string) => {
    this.showProductSizes = {
      ...this.showProductSizes,
      [productId]: flag
    }
  }

  getBookedDoors = computedFn((productId: string, doorOrders: any) => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const bookedDoorOrderIds = filter(
      Object.keys(product?.quantity?.doorProducts || {}),
      doorOrderId => !find(doorOrders, doorOrder => doorOrder.id === doorOrderId)?.deleted
    )
    return bookedDoorOrderIds
  })

  @computed get areObjectsDataLoading() {
    return this.dataStore.itemIndexObjectStore.objectLoadingProgress.every(map =>
      map.get('LOADING')
    )
  }

  getSumOfDraftQuantityOfBookedDoors = computedFn((productId: string) => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const value = reduce(
      product?.quantity?.doorProducts,
      (acc, door) => acc + (door?.totalByDraftQuantity || 0),
      0
    )
    return value
  })

  getSizes = computedFn((productId: string): Array<ISize> => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const {
      original: { sizes },
      quantity
    } = product
    return (sizes || [])
      .concat(
        (quantity?.inactiveSizes || []).map(inactiveSize => {
          return { ...inactiveSize, isInactive: true }
        })
      )
      .sort((sizeA, sizeB) => sizeA.sizeCode - sizeB.sizeCode)
  })

  isUnsizedProduct = computedFn((productId: string): boolean => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const {
      original: { sizes }
    } = product
    return isEmpty(sizes)
  })

  getProductCurves = computedFn((productId: string) => {
    const {
      queryParams: { clientId, buyingSessionGroupId }
    } = nav
    const curves = productCurveStore.getCurvesForProduct(
      productId,
      clientId,
      buyingSessionGroupId
    )
    return {
      sizeCurve: curves?.sizeCurve ?? {},
      doorCurve: curves?.doorCurve ?? {}
    }
  })

  getProductSizeCurve = computedFn((productId: string, doorId: string) => {
    const sizeCurves = { ...(this.getProductCurves(productId).sizeCurve ?? {}) }
    const sizes = this.getSizes(productId)
    // filter Size curve by active sizes
    const calculatedSizeCurves = sizes.reduce(
      (acc, { name, id, isInactive }) => {
        return {
          ...acc,
          curve: {
            ...(acc?.curve || {}),
            ...(sizeCurves?.curves?.[name]
              ? isInactive
                ? {}
                : {
                    [name]: sizeCurves.curves?.[name]
                  }
              : {})
          }
        }
      },
      { curve: {}, doors: {} }
    )
    calculatedSizeCurves.doors = Object.keys(sizeCurves?.doors || {}).reduce(
      (acc, doorId) => ({
        ...acc,
        [doorId]: sizes.reduce((acc, { name, isInactive }) => {
          return {
            ...acc,
            ...(sizeCurves?.doors?.[doorId]
              ? isInactive
                ? {}
                : {
                    [name]: sizeCurves?.doors?.[doorId]?.[name]
                  }
              : {})
          }
        }, {})
      }),
      {}
    )
    const productCurve = { ...calculatedSizeCurves.curve }
    const doorCurve = calculatedSizeCurves?.doors?.[doorId] || {}

    const curves = !isEmpty(doorCurve) ? doorCurve : productCurve
    const curveTotal = sum(values(curves))
    if (curveTotal) {
      return Object.keys(curves).reduce(
        (acc, key) => ({ ...acc, [key]: curves[key] / curveTotal }),
        {}
      )
    }
    return {}
  })

  getProductDoorCurveByGrading = computedFn(
    (productId: string, activeDoorOrderIds: any) => {
      const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
      const {
        quantity: { doorProducts = {}, gradingAttributes = {} }
      } = product
      const doorCurves = { ...(this.getProductCurves(productId).doorCurve ?? {}) }
      const grade = gradingAttributes?.grade ?? ''
      const doorCurveByGrading = grade
        ? doorCurves?.curves?.[grade] ?? doorCurves?.curves
        : doorCurves?.curves ?? {}
      const calculatedDoorCurves = activeDoorOrderIds.reduce(
        (acc, key) => {
          const doorId = doorProducts[key].doorId
          return {
            ...acc,
            curve: {
              ...(acc?.curve || {}),
              ...(has(doorCurveByGrading, doorId)
                ? {
                    [doorId]: doorCurveByGrading?.[doorId]
                  }
                : {})
            }
          }
        },
        { curve: {}, doors: {} }
      )
      const curves = { ...calculatedDoorCurves.curve }
      const curveTotal = sum(values(curves))
      if (curveTotal) {
        return Object.keys(curves).reduce(
          (acc, key) => ({ ...acc, [key]: curves[key] / curveTotal }),
          {}
        )
      }
      return {}
    }
  )

  getQuantitiesBySizeCurve = (
    inputValue: number,
    sizes: any[],
    curves: any,
    oldSizeValues: any
  ) => {
    return sizes.map(size => {
      const curve = curves[size.name]
      if (curve) {
        return {
          sizeId: size.id,
          quantity: this.getQuantityByCurve(inputValue, curve)
        }
      }
      return {
        sizeId: size.id,
        quantity: oldSizeValues[size.id]?.quantity || 0
      }
    }, {})
  }

  getQuantityByCurve = (value: number, curve: number) => {
    return Math.round(value * curve)
  }

  getDoorQuantitiesByDoorCurve = (inputValue: number, doors: any, curves: any) => {
    return Object.keys(doors || {}).reduce((acc, door) => {
      const doorId = doors[door].doorId
      const doorOrderId = doors[door].doorOrderId
      const curve = curves[doorId]
      return {
        ...acc,
        ...(curve
          ? { [doorOrderId]: this.getQuantityByCurve(inputValue, curve) }
          : {
              [doorOrderId]: curve === 0 ? curve : doors[door]?.totalByDraftQuantity || 0
            })
      }
    }, {})
  }

  @action
  async handleDoorQtyUpdate(productId: string, doorOrderId: string, newValue: number) {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const {
      id,
      quantity: { doorProducts = {} }
    } = product
    const sizes = this.getSizes(productId)
    const payload = {
      doorOrderId,
      totalDraftQty: newValue,
      sizeQuantities: this.getQuantitiesBySizeCurve(
        newValue,
        sizes,
        this.getProductSizeCurve(productId, doorProducts[doorOrderId].doorId),
        doorProducts[doorOrderId].sizeQuantities || {}
      ),
      isUnsized: !sizes?.length
    }
    await this.dataStore.updateDoorOrderDraftQuantity(id, payload)
  }

  @action
  async handleDoorSizeQtyUpdate(
    productId: string,
    doorOrderId: string,
    newValue: number,
    sizeId: string
  ) {
    await this.dataStore.updateDoorOrderDraftQuantity(
      productId,
      {
        doorOrderId,
        newValue,
        sizeId
      },
      { isSizeUpdate: true }
    )
  }

  @action
  bookUnbookDoors = async (clientOrderId, productId, doorOrderIds, isBook) => {
    const {
      queryParams: { clientType, subSection, bspId }
    } = nav
    if (
      !bspId &&
      (subSection === ListTableView.Summary ||
        (getEnumValue(VIPClientType.Internal) === clientType && !subSection))
    ) {
      this.dataStore.bookUnBookDoorsForSummary(
        clientOrderId,
        productId,
        doorOrderIds,
        isBook,
        false
      )
    }
    // for Quantity
    else {
      await this.dataStore.bookUnBookDoors(
        clientOrderId,
        productId,
        doorOrderIds,
        isBook,
        false
      )
    }
  }

  @action
  bulkUpdateDraftQuantity = async (summary, productId, value) => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const {
      quantity: { doorProducts = {} }
    } = product
    const doorOrderIds = Object.keys(doorProducts)
    const activeDoorOrderIds = filter(
      doorOrderIds,
      doorOrderId =>
        !find(summary?.doorOrders, doorOrder => doorOrder.id === doorOrderId)?.deleted
    )
    //Door Curves
    const productDoorCurves = this.getProductDoorCurveByGrading(
      productId,
      activeDoorOrderIds
    )
    const doorQuantitiesByCurve = this.getDoorQuantitiesByDoorCurve(
      value,
      doorProducts,
      productDoorCurves
    )
    const sizes = this.getSizes(productId)
    const payload = {
      isUnsized: !sizes?.length,
      doorOrderWizeQuanties: activeDoorOrderIds.map(doorOrderId => ({
        doorOrderId,
        totalByDraftQuantity: doorQuantitiesByCurve[doorOrderId] ?? 0,
        sizeQuantities: this.getQuantitiesBySizeCurve(
          doorQuantitiesByCurve[doorOrderId] ?? 0,
          sizes,
          this.getProductSizeCurve(productId, doorProducts[doorOrderId].doorId),
          doorProducts[doorOrderId].sizeQuantities || {}
        )
      }))
    }
    await this.dataStore.bulkUpdateDraftQuantity(
      summary.clientOrder.clientOrderId,
      productId,
      payload
    )
  }

  @action
  updateComment = (productId: string, value: string, commentType: string) => {
    this.dataStore.updateComment(productId, value, commentType)
  }

  private mapToCopiedObject = (product, doorsOrdersAndDoors) => {
    return {
      id: product.id,
      original: {
        externalImages: {
          img_Thumbnail: product.original?.externalImages?.img_Thumbnail
            ? encodeURIComponent(product.original?.externalImages?.img_Thumbnail)
            : null
        },
        style: {
          modelDescription: product.original?.style.modelDescription
        },
        defaultImage: product?.original?.defaultImage,
        sizes: map(this.getSizes(product.id), size => omitSingle('isInactive', size))
      },
      quantity: {
        totalByDraftQuantity: product.quantity.totalByDraftQuantity,
        totalBySizeQuantity: product.quantity.totalBySizeQuantity,
        doorProducts: {
          ...Object.keys(product.quantity.doorProducts).reduce((acc, key) => {
            const { totalByDraftQuantity, totalBySizeQuantity, sizeQuantities } =
              product.quantity.doorProducts[key]
            return {
              ...acc,
              [doorsOrdersAndDoors[key]]: {
                totalByDraftQuantity,
                totalBySizeQuantity,
                sizeQuantities: mapValues(sizeQuantities, ({ quantity }) => ({
                  quantity
                }))
              }
            }
          }, {})
        }
      }
    }
  }

  protected mapFromCopiedObject = (copiedValue, doorNameAndDoorOrderMap) => {
    return {
      id: copiedValue.id,
      original: {
        externalImages: {
          img_Thumbnail: decodeURIComponent(
            copiedValue.original?.externalImages?.img_Thumbnail
          )
        },
        style: {
          modelDescription: copiedValue.original?.style.modelDescription
        },
        defaultImage: copiedValue?.original?.defaultImage,
        sizes: copiedValue?.original?.sizes
      },
      quantity: {
        totalByDraftQuantity: copiedValue.quantity.totalByDraftQuantity,
        totalBySizeQuantity: copiedValue.quantity.totalBySizeQuantity,
        doorProducts: {
          ...Object.keys(copiedValue.quantity.doorProducts).reduce((acc, doorName) => {
            const { totalByDraftQuantity, totalBySizeQuantity, sizeQuantities } =
              copiedValue.quantity.doorProducts[doorName]
            return {
              ...acc,
              [doorNameAndDoorOrderMap[doorName]]: {
                totalByDraftQuantity,
                totalBySizeQuantity,
                sizeQuantities: mapValues(sizeQuantities, ({ quantity }, sizeId) => ({
                  quantity,
                  sizeId
                }))
              }
            }
          }, {})
        }
      }
    }
  }

  getActiveColumn = (
    rowIndex: number,
    colIndex: number,
    visibleColumns: Column[]
  ): number => {
    do {
      const column: Column =
        this.elementRefStore.tableRef.getColumnByVisibleIndex(colIndex)
      const rowData = this.dataStore.itemIndexObjectStore.indexObjectMap.get(rowIndex)
      if (rowData?.quantity?.doorProducts?.[column.field]) {
        break
      }
      colIndex++
    } while (colIndex !== visibleColumns.length)
    return colIndex
  }

  getPrevActiveColumn = (rowIndex: number, colIndex: number): number => {
    do {
      const column: Column =
        this.elementRefStore.tableRef.getColumnByVisibleIndex(colIndex)
      if (!column) {
        return -1
      }
      const rowData = this.dataStore.itemIndexObjectStore.indexObjectMap.get(rowIndex)
      if (rowData?.quantity?.doorProducts?.[column.field]) {
        return colIndex
      }
      colIndex--
    } while (colIndex !== 0)
    return -1
  }

  @action
  navigateToNextEditableCell = (curRowIndex?: number, curColIndex?: number) => {
    const visibleColumns = this.elementRefStore.tableRef.flattenedVisibleColumns
    const lastRowIndex = this.dataStore.itemIndexObjectStore.objects.length
    const rowIndexToStartFrom = lastRowIndex === curRowIndex ? 0 : curRowIndex
    if (visibleColumns.length) {
      if (curColIndex !== visibleColumns.length - 1) {
        const colIndex = this.getActiveColumn(
          rowIndexToStartFrom,
          curColIndex + 1,
          visibleColumns
        )
        // Check if any cell in next columns in current row is editable
        if (colIndex !== visibleColumns.length) {
          this.activeItemStore.setSizeIndex(-1)
          this.navigateToCell(rowIndexToStartFrom, colIndex)
          return
        }
      }
      // Means no cell is editable in current row
      let rowIndex = rowIndexToStartFrom + 1
      while (rowIndex !== lastRowIndex) {
        const colIndex = this.getActiveColumn(rowIndex, 0, visibleColumns)
        // Check if any cell in next columns in current row is editable
        if (colIndex !== visibleColumns.length) {
          this.activeItemStore.setSizeIndex(-1)
          this.navigateToCell(rowIndex, colIndex)
          return
        }
        rowIndex++
      }
      // It would be last row
      this.navigateToNextEditableCell(0, 0)
    }
  }

  @action
  navigateToPrevAvailableCell = (curRowIndex?: number, curColIndex?: number) => {
    const visibleColumns = this.elementRefStore.tableRef.flattenedVisibleColumns
    const rowIndexToStartFrom = curRowIndex
    if (visibleColumns.length) {
      const colIndex = this.getPrevActiveColumn(rowIndexToStartFrom, curColIndex - 1)
      // Check if any cell in next columns in current row is editable
      if (colIndex !== -1) {
        this.activeItemStore.setSizeIndex(-1)
        this.navigateToCell(rowIndexToStartFrom, colIndex)
        return
      }

      if (rowIndexToStartFrom === 0) {
        return
      }
      // Means no cell is editable in current row
      let rowIndex = rowIndexToStartFrom - 1
      do {
        const colIndex = this.getPrevActiveColumn(rowIndex, visibleColumns.length - 1)
        // Check if any cell in next columns in current row is editable
        if (colIndex !== -1) {
          this.navigateToCell(rowIndex, colIndex)
          this.activeItemStore.setSizeIndex(-1)
          return
        }
        rowIndex--
      } while (rowIndex !== -1)
    }
  }

  @action
  refresh = async () => {
    if (this.elementRefStore.ref?.getScrollTop) {
      const scrollPosition = this.elementRefStore.ref.getScrollTop()
      const scrolledNodeId = this.scrollPositionStore.scrolledNode?.id
      if (!scrolledNodeId) {
        return
      }
      const [, index] = extractIndexFromId(scrolledNodeId)
      transaction(async () => {
        await this.dataStore.fetchHierarchy()
        this.dataStore.itemIndexObjectStore.clearAll()
        if (this.dataStore.heirarchy.length) {
          this.activeItemStore.setIndex(index)
          this.scrollPositionStore.scrollToNode(scrolledNodeId)
          this.elementRefStore.ref.setScrollTop(scrollPosition)
        }
      })
    }
  }

  @computed get copiedTemplateQuantities() {
    return orderTemplatePreferencesStore.templates?.clipboard?.quantity?.quantities
  }

  @action
  copyTemplate = (productId: string, { name, description = '' }, doorOrdersAndDoors) => {
    const product = this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const doorOrderAndDoorNameMap = doorOrdersAndDoors.reduce(
      (acc, doorOrder) => ({
        ...acc,
        [doorOrder.id]: doorOrder.door.name
      }),
      {}
    )
    const templatePreferences = orderTemplatePreferencesStore.templates

    const payloadToSave = {
      [OrderTemplatePreferences.TEMPLATES_PREFERENCE]: {
        clipboard: {
          quantity: {
            ...templatePreferences?.clipboard?.quantity,
            quantities: [
              {
                copyMetaData: { name, description },
                ...this.mapToCopiedObject(product, doorOrderAndDoorNameMap)
              },
              ...(templatePreferences?.clipboard?.quantity?.quantities || [])
            ]
          }
        }
      }
    }
    orderTemplatePreferencesStore.updatedOrderPreferences(payloadToSave)
  }

  @action
  pasteTemplateQuantity = async (
    clientOrderId: string,
    productId: string,
    name: string,
    doorOrdersAndDoors
  ) => {
    const productToPasteIn =
      this.dataStore.itemIndexObjectStore.idObjectMap.get(productId)
    const templatePreferences =
      orderTemplatePreferencesStore.templates?.clipboard?.quantity

    let quantity = templatePreferences?.['default']
    if (name !== 'default') {
      quantity = find(templatePreferences?.['quantities'], ['copyMetaData.name', name])
    }
    if (!quantity) {
      return
    }
    const doorNameAndDoorOrderMap = doorOrdersAndDoors.reduce(
      (acc, doorOrder) => ({
        ...acc,
        [doorOrder.door.name]: doorOrder.id
      }),
      {}
    )
    const cleanedQuantity = this.mapFromCopiedObject(quantity, doorNameAndDoorOrderMap)
    const sizes = this.getSizes(productId)
    const mergedDoorProducts = mapValues(
      productToPasteIn.quantity.doorProducts,
      (originalDoorOrder, doorOrderId) => ({
        ...originalDoorOrder,
        ...cleanedQuantity.quantity?.doorProducts?.[doorOrderId],
        sizeQuantities: map(sizes, ({ id, isInactive }) => ({
          sizeId: id,
          ...(isInactive
            ? {
                sizeId: id,
                quantity:
                  productToPasteIn?.quantity?.doorProducts?.[doorOrderId]
                    ?.sizeQuantities?.[id]?.quantity || 0
              }
            : !isNil(
                cleanedQuantity.quantity?.doorProducts?.[doorOrderId]?.sizeQuantities?.[
                  id
                ]?.quantity
              )
            ? {
                sizeId: id,
                quantity:
                  cleanedQuantity.quantity?.doorProducts?.[doorOrderId]?.sizeQuantities?.[
                    id
                  ]?.quantity || 0
              }
            : {
                sizeId: id,
                quantity:
                  productToPasteIn?.quantity?.doorProducts?.[doorOrderId]
                    ?.sizeQuantities?.[id]?.quantity || 0
              })
        }))
      })
    )

    const payload = {
      isUnsized: !sizes?.length,
      doorOrderWizeQuanties: filter(
        map(
          mergedDoorProducts,
          ({ doorOrderId, totalByDraftQuantity, sizeQuantities }) => ({
            doorOrderId,
            totalByDraftQuantity,
            sizeQuantities: values(sizeQuantities)
          })
        ),
        doorProduct =>
          !find(
            doorOrdersAndDoors,
            doorOrder => doorOrder?.id === doorProduct?.doorOrderId
          )?.deleted
      )
    }
    await this.dataStore.bulkUpdateDraftQuantity(clientOrderId, productId, payload)
  }

  @action
  deleteCopiedTemplate = name => {
    const quantities =
      orderTemplatePreferencesStore.templates?.clipboard?.quantity?.quantities || []

    const updatedQuantities =
      differenceBy(quantities, [{ copyMetaData: { name } }], 'copyMetaData.name') || []

    const payloadToSave = {
      [OrderTemplatePreferences.TEMPLATES_PREFERENCE]: {
        clipboard: {
          quantity: {
            ...orderTemplatePreferencesStore.templates?.clipboard?.quantity,
            quantities: updatedQuantities
          }
        }
      }
    }
    orderTemplatePreferencesStore.updatedOrderPreferences(payloadToSave)
  }
}
